// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getIsNonPurchasable } from "@keller/App/Store/Selectors/GeneralSelectors";
import Cart from "@keller/XDL/Icons/Cart";
import { color, mergeWithCss } from "@keller/XDL/Utilities";
import React, { NamedExoticComponent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    // XNG-Changes
    iconOnly?: boolean;
    iconOnlyExtendedStyles?: ButtonPresentationProps;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        isNonPurchasable: getIsNonPurchasable(state),
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {
    mergeCss: true,
    sizeVariant: "small",
    css: css`
        position: relative;
        line-height: 12px;
        &.loading,
        &.success {
            color: transparent;
            &:disabled {
                background: ${color("primary", 0.8)};
                opacity: 1;
            }
        }

        &.success > ${TypographyStyle} {
            color: ${color("common.white")};
            left: 0;
            right: 0;
            text-align: center;
            position: absolute;
        }
        & .center {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%);
        }
    `,
};

export const productAddToCartIconButtonStyles: ButtonPresentationProps = {
    ...mergeWithCss(productAddToCartButtonStyles, {
        css: css`
            padding: 2px 14px;
            min-height: 30px;
            min-width: 52px;
        `,
    }),
};

interface AddToCartStatusInfoInterface {
    status: string;
    label: string;
    icon: NamedExoticComponent<{}> | string;
}

// XNG-Changes
const dotsStyleColor = "common.white";
const dotsStyle = {
    css: css`
        position: relative;
        left: -9999px;
        width: 3px;
        height: 3px;
        border-radius: 5px;
        background-color: ${color(dotsStyleColor)};
        color: ${color(dotsStyleColor)};
        box-shadow: 9999px 0 0 -5px ${color(dotsStyleColor)};
        animation: dotPulse 1.5s infinite linear;
        animation-delay: 0.25s;

        &::before,
        &::after {
            content: "";
            display: inline-block;
            position: absolute;
            top: 0;
            width: 3px;
            height: 3px;
            border-radius: 5px;
            background-color: ${color(dotsStyleColor)};
            color: ${color(dotsStyleColor)};
        }

        &::before {
            box-shadow: 9984px 0 0 -5px ${color(dotsStyleColor)};
            animation: dotPulseBefore 1.5s infinite linear;
            animation-delay: 0s;
        }

        &::after {
            box-shadow: 10014px 0 0 -5px ${color(dotsStyleColor)};
            animation: dotPulseAfter 1.5s infinite linear;
            animation-delay: 0.5s;
        }

        @keyframes dotPulseBefore {
            0% {
                box-shadow: 9984px 0 0 -5px ${color(dotsStyleColor)};
            }
            30% {
                box-shadow: 9984px 0 0 2px ${color(dotsStyleColor)};
            }
            60%,
            100% {
                box-shadow: 9984px 0 0 -5px ${color(dotsStyleColor)};
            }
        }

        @keyframes dotPulse {
            0% {
                box-shadow: 9999px 0 0 -5px ${color(dotsStyleColor)};
            }
            30% {
                box-shadow: 9999px 0 0 2px ${color(dotsStyleColor)};
            }
            60%,
            100% {
                box-shadow: 9999px 0 0 -5px ${color(dotsStyleColor)};
            }
        }

        @keyframes dotPulseAfter {
            0% {
                box-shadow: 10014px 0 0 -5px ${color(dotsStyleColor)};
            }
            30% {
                box-shadow: 10014px 0 0 2px ${color(dotsStyleColor)};
            }
            60%,
            100% {
                box-shadow: 10014px 0 0 -5px ${color(dotsStyleColor)};
            }
        }
    `,
};

interface AddToCartStatusInterface {
    default: AddToCartStatusInfoInterface;
    loading: AddToCartStatusInfoInterface;
    success: AddToCartStatusInfoInterface;
}

const addToCartStatus: AddToCartStatusInterface = {
    default: {
        status: "default",
        label: "Add to Cart",
        icon: "",
    },
    loading: {
        status: "loading",
        label: "",
        icon: "",
    },
    success: {
        status: "success",
        label: "Added",
        icon: "",
    },
};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    iconOnly,
    iconOnlyExtendedStyles,
    isNonPurchasable,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeWithCss(productAddToCartButtonStyles, extendedStyles));
    const [iconStyles] = useState(() => mergeWithCss(productAddToCartIconButtonStyles, iconOnlyExtendedStyles));
    const [status, setStatus] = useState(addToCartStatus.default);

    // XNG-Changes
    useEffect(() => {
        if (status === addToCartStatus.success) {
            setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 800);
        }
    }, [status]);

    //   Xng-changes : hide for web-only
    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart || isNonPurchasable) {
        return null;
    }

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();
        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        // XNG-Changes
        setStatus(addToCartStatus.loading);

        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        // XNG-Changes
        setStatus(addToCartStatus.success);

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
        }
    };

    /**
     * XNG-Changes
     * to prevent button width change when the status change, we use position absolute and text-color: transparent
     */
    if (iconOnly) {
        return (
            <Button
                {...iconStyles}
                onClick={addToCartClickHandler}
                className={`button-wrapper-icon-only ${status.status}`}
                disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled || status !== addToCartStatus.default}
                {...otherProps}
            >
                {status === addToCartStatus.loading ? (
                    <StyledWrapper className="center">
                        <StyledWrapper {...dotsStyle} />
                    </StyledWrapper>
                ) : (
                    <Icon src={Cart} size={19} color="common.white" />
                )}
            </Button>
        );
    }

    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled || status !== addToCartStatus.default}
            className={`${status.status}`}
            {...otherProps}
        >
            {status === addToCartStatus.loading && (
                <StyledWrapper className="center">
                    <StyledWrapper {...dotsStyle} />
                </StyledWrapper>
            )}
            {labelOverride ?? translate("Add to Cart")}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
